import axios from 'axios'
import config from './config'

const responseBody = res => res.data
export type EntityType = 'COMPANY' | 'LOCATION'
const getHeader = () => {
  return {
    Authorization: `Bearer ${apiKey}`,
    Channel: 'APP',
    Source: 'WEB_USER',
    Version: '2021-04-15',
  }
}

const queryType = (entityType: EntityType) => {
  return entityType === 'COMPANY' ? 'companyId' : 'locationId'
}

export interface DedicateIpApiDto {
  companyId: string;
  entity: EntityType;
  locationId?: string;
}

let apiKey: string

const requests = {
  get: url =>
    axios({
      url: url,
      baseURL: config.restApiUrl,
      method: 'GET',
      headers: getHeader(),
    }).then(responseBody),
  post: (url, body) =>
    axios({
      url: url,
      baseURL: config.restApiUrl,
      method: 'POST',
      headers: getHeader(),
      data: body,
    }).then(responseBody),
  put: (url, body) =>
    axios({
      url: config.restApiUrl + url,
      method: 'PUT',
      headers: getHeader(),
      data: body,
    }).then(responseBody),
  delete: url =>
    axios({
      url: config.restApiUrl + url,
      method: 'DELETE',
      headers: getHeader(),
    }).then(responseBody),
}

const Mailgun = {
  list: (companyId, deleted, skip?, limit?, query?) =>
    requests.get(
      `/conversations/providers/mailgun?companyId=${companyId}&deleted=${deleted}${
        skip ? `&skip=${skip}` : ''
      }${limit ? `&limit=${limit}` : ''}${
        query ? `&query=${encodeURIComponent(query)}` : ''
      }`
    ),
  add: (data: { [key: string]: any }, companyId?, locationId?) =>
    requests.post(
      `/conversations/providers/mailgun${
        companyId ? `?companyId=${companyId}` : ''
      }${locationId ? `?locationId=${locationId}` : ''}`,
      data
    ),
  edit: (data: { [key: string]: any }, companyId?, locationId?) =>
    requests.put(
      `/conversations/providers/mailgun${
        companyId ? `?companyId=${companyId}` : ''
      }${locationId ? `?locationId=${locationId}` : ''}`,
      data
    ),
  delete: (companyId?, locationId?) =>
    requests.delete(
      `/conversations/providers/mailgun${
        companyId ? `?companyId=${companyId}` : ''
      }${locationId ? `?locationId=${locationId}` : ''}`
    ),
  fetch: (companyId?, locationId?) =>
    requests.get(
      `/conversations/providers/mailgun/fetch${
        companyId ? `?companyId=${companyId}` : ''
      }${locationId ? `?locationId=${locationId}` : ''}`
    ),
  fetchDomain: locationId =>
    requests.get(
      `/conversations/providers/mailgun/fetch/domain?locationId=${locationId}`
    ),
  validations: (data: { [key: string]: any }, locationId) =>
    requests.post(
      `/conversations/providers/mailgun/validations?locationId=${locationId}`,
      data
    ),
}

const TwilioAccount = {
  list: (companyId, deleted, skip?, limit?, query?) =>
    requests.get(
      `/conversations/providers/twilio?companyId=${companyId}&deleted=${deleted}${
        skip ? `&skip=${skip}` : ''
      }${limit ? `&limit=${limit}` : ''}${
        query ? `&query=${encodeURIComponent(query)}` : ''
      }`
    ),
  edit: (data: { [key: string]: any }, companyId?, locationId?) =>
    requests.post(
      `/conversations/providers/twilio${
        companyId ? `?companyId=${companyId}` : ''
      }${locationId ? `?locationId=${locationId}` : ''}`,
      data
    ),
  delete: (companyId?, locationId?, close?) =>
    requests.delete(
      `/conversations/providers/twilio${
        companyId ? `?companyId=${companyId}` : ''
      }${locationId ? `?locationId=${locationId}` : ''}${
        close === 'cancel' ? '&dont_close_twilio=true' : ''
      }`
    ),
  fetch: (companyId?, locationId?) =>
    requests.get(
      `/conversations/providers/twilio/fetch${
        companyId ? `?companyId=${companyId}` : ''
      }${locationId ? `?locationId=${locationId}` : ''}`
    ),
  createSubAccount: locationId =>
    requests.post(
      `/conversations/providers/twilio/setup/subaccount?locationId=${locationId}`,
      {}
    ),
}

const EmailISV = {
  setIsvStatus: (body: {
    companyId?: string
    locationId?: string
    emailCharges?: number
    domain?: string
  }) => requests.post('/email-isv/set/status', { ...body }),
  migrateToLcEmail: (companyId?, locationId?) =>
    requests.post(
      `/email-isv/migrate${companyId ? `?companyId=${companyId}` : ''}${
        locationId ? `?locationId=${locationId}` : ''
      }`,
      {}
    ),
  toggleUnsubscribeLanguageForLocation: (
    locationIds: string[],
    companyId: string
  ) =>
    requests.put(`/email-isv/provider/mailgun/emailUnsubscribe/toggle`, {
      locationIds,
      companyId,
    }),
  updateUnsubscribeLanguage: (
    locationIds: string[],
    companyId: string,
    unsubscribeMessage: string
  ) =>
    requests.put(`/email-isv/provider/mailgun/emailUnsubscribe/update`, {
      locationIds,
      companyId,
      unsubscribeMessage,
    }),
  toggleSmtpControl: (locationId: string, blockAddSmtp: boolean) =>
    requests.post(`/email-isv/toggle-smtp-control`, {
      locationId,
      blockAddSmtp,
    }),
  toggleReValidation: (
    locationId: string,
    validateEmails: boolean,
    reverifyEmails: boolean
  ) =>
    requests.post(
      `/email-isv/feature/verification/toggle?locationId=${locationId}`,
      {
        validateEmails,
        reverifyEmails,
      }
    ),
  getSmtpConnections: (entityType: EntityType, entityId: string) =>
    requests.get(
      `/email-isv/smtp-connections?${queryType(entityType)}=${entityId}`
    ),
  verifyContact: (locationId: string, contactId: string, override: boolean) => requests.post(`/email-isv/feature/verification/verify/contact?locationId=${locationId ? locationId : ''}&contactId=${contactId ? contactId: ''}`, {
    override
  }),
  fetchContactVerification: (email: string) => requests.post(`/email-isv/feature/verification/result`,{
    email
  }),
  getDedicatedIps: (body: DedicateIpApiDto) => {
    let url = `/email-isv/feature/ip/marketplace/alloted/${body.entity}?companyId=${body.companyId}`;
    if (body.entity === 'LOCATION') {
      if (!body.locationId) {
        throw Error(`Please provide a locationId`);
      }
      url = url + `&locationId=${body.locationId}`;
    }
    return requests.get(url);
  },
  verifyNotificationDomain: (entityId, entityType, domain) => {
    return requests.post(
      `/email-isv/feature/domain/notification-domain-verify?${queryType(entityType)}=${entityId}&type=notification`,
      {
        domain,
      }
    );
  },
  verifyDedicatedDomain: (entityId, entityType, domain) => {
    return requests.post(
      `/email-isv/feature/domain/verify?${queryType(entityType)}=${entityId}`,
      {
        domain,
      }
    );
  }
}

const Workflows = {
  getNotifications: (locationId: string) => {
    return requests.get(`/workflows/notifications/${locationId}`)
  }
}

export default {
  setApiKey: _apiKey => (apiKey = _apiKey),
  Mailgun,
  TwilioAccount,
  EmailISV,
  Workflows
}
